import CryptoJS from 'crypto-js';
import axios from 'axios';
import { BASE_URL } from '@/config/api';
import {
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
  clearAccessToken,
  clearRefreshToken,
} from '@/utils/token_functions';

const ENCRYPTION_ENABLED = import.meta.env.VITE_ENABLE_ENCRYPTION === 'true';

export function encryptJson(data) {
  const key = CryptoJS.lib.WordArray.random(32);
  const iv  = CryptoJS.lib.WordArray.random(16);

  const encrypted = CryptoJS.AES.encrypt(JSON.stringify(data), key, {
    iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });

  return {
    vi:      key.toString(CryptoJS.enc.Hex),  // the AES key
    iv:      iv.toString(CryptoJS.enc.Hex),   // the initialisation vector
    content: encrypted.toString(),
  };
}

export function decryptJson(encryptedData) {
  try {
    const iv      = CryptoJS.enc.Hex.parse(encryptedData.iv);
    const key     = CryptoJS.enc.Hex.parse(encryptedData.vi);
    const content = encryptedData.content;

    const decrypted = CryptoJS.AES.decrypt(content, key, {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    return JSON.parse(decrypted.toString(CryptoJS.enc.Utf8));
  } catch (error) {
    console.error('DECRYPTION FAILED:', error);
    throw error;
  }
}

function isEncryptedResponse(data) {
  return (
    ENCRYPTION_ENABLED &&
    data &&
    data.vi &&
    data.iv &&
    data.content
  );
}

function maybeDecryptResponse(response) {
  if (isEncryptedResponse(response.data)) {
    response.data = decryptJson(response.data);
  }
  return response;
}


export const secureAxios = {
  post: (url, data, config = {}) => {
    if (!ENCRYPTION_ENABLED) {
      return axios.post(url, data, config);
    }
    return axios
      .post(url, encryptJson(data), config)
      .then(maybeDecryptResponse);
  },

  put: (url, data, config = {}) => {
    if (!ENCRYPTION_ENABLED) {
      return axios.put(url, data, config);
    }
    return axios
      .put(url, encryptJson(data), config)
      .then(maybeDecryptResponse);
  },

  patch: (url, data, config = {}) => {
    if (!ENCRYPTION_ENABLED) {
      return axios.patch(url, data, config);
    }
    return axios
      .patch(url, encryptJson(data), config)
      .then(maybeDecryptResponse);
  },

  delete: (url, config = {}) => {
    if (ENCRYPTION_ENABLED && config.data) {
      config = { ...config, data: encryptJson(config.data) };
    }
    return axios.delete(url, config).then(maybeDecryptResponse);
  },

  get: (url, config = {}) => {
    return axios.get(url, config).then(maybeDecryptResponse);
  },

  head:    axios.head,
  options: axios.options,
};

let _refreshPromise = null;

axios.interceptors.response.use(
  (response) => response,

  async (error) => {
    const originalRequest = error.config;

    // Skip refresh for the refresh call itself and for credential auth flows
    // (login/OTP 401 = bad credentials, not an expired session).
    const url = originalRequest?.url || '';
    const isRefreshEndpoint = url.includes('/v1/auth/refresh');
    const isCredentialAuthEndpoint = [
      '/v1/auth/login',
      '/v1/auth/register',
      '/v1/auth/verify-authentication',
      '/v1/auth/send-code-to-email',
      '/v1/auth/forgot-password',
      '/v1/auth/recovery',
      '/v1/auth/facebook',
      '/v1/auth/google',
      '/v1/auth/x',
    ].some((path) => url.includes(path));
    const is401 = error.response?.status === 401;

    if (
      !is401 ||
      originalRequest?._retried ||
      isRefreshEndpoint ||
      isCredentialAuthEndpoint
    ) {
      return Promise.reject(error);
    }

    originalRequest._retried = true;

    try {
      if (!_refreshPromise) {
        _refreshPromise = (async () => {
          const refreshToken = getRefreshToken();
          if (!refreshToken) throw new Error('No refresh token available');

          const payload = ENCRYPTION_ENABLED
            ? encryptJson({ refresh_token: refreshToken })
            : { refresh_token: refreshToken };

          const res = await axios.post(
            `${BASE_URL}/v1/auth/refresh`,
            payload,
            { headers: { 'Content-Type': 'application/json' } }
          );

          const data = isEncryptedResponse(res.data) ? decryptJson(res.data) : res.data;

          const newAccessToken  = data?.access_token  || data?.data?.access_token;
          const newRefreshToken = data?.refresh_token || data?.data?.refresh_token;

          if (!newAccessToken) throw new Error('Refresh response missing access_token');

          setAccessToken(newAccessToken);
          if (newRefreshToken) {
            setRefreshToken(newRefreshToken);
          }

          return newAccessToken;
        })().finally(() => {
          _refreshPromise = null;
        });
      }

      const newAccessToken = await _refreshPromise;

      originalRequest.headers = {
        ...originalRequest.headers,
        Authorization: `Bearer ${newAccessToken}`,
      };

      return axios(originalRequest);
    } catch (refreshError) {
      clearAccessToken();
      clearRefreshToken();

      // Stamp so errorMiddleware can force logout without the generic modal.
      if (refreshError.response) {
        refreshError.response._refreshFailed = true;
        if (
          refreshError.response.data &&
          typeof refreshError.response.data === 'object'
        ) {
          refreshError.response.data._refreshFailed = true;
        }
      } else {
        refreshError._refreshFailed = true;
      }

      return Promise.reject(refreshError);
    }
  }
);

const encryptionMiddleware = () => (next) => (action) => {
  return next(action);
};

export default encryptionMiddleware;
